import {
	AlertBar,
	Button,
	Checkbox,
	DatePicker,
	FormItem,
	InputRow,
	MultiSelect,
	MultiSelectItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError
} from "client/Components";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import { AnnouncementSaveModel, AnnouncementStatus } from "client/Api/Announcements/announcements";
import { TagKind } from "client/Api/Tags/tags";
import { HubTagSelect } from "client/Features/Hub/Builder/HubTagSelect";
import { TimeZone } from "client/DateAndTime/timezones";
import { ICollectionInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import { HubHierarchyListNodeModel } from "client/Api/Hub/common.composer";

const {
	Title,
	Step,
	Content,
	FormContainer
} = HubItemComposerDialog;

export type SettingsStepProps = {
	isActive: boolean;
	attachable: IValueInputAttachable<AnnouncementSaveModel>;
	status: AnnouncementStatus;
	categoryHierarchy: readonly HubHierarchyListNodeModel[];
	isEditingExistingAnnouncement: boolean;
	hasPermissionToCreateNewCategory: boolean;
	hasPermissionToSendDirectPopups: boolean;
	onCreateNewCategory(): Promise<void>;
	timezone: TimeZone;
	hasStartTime: boolean;
	onHasStartTimeChange(value: boolean): void;
	hasEndTime: boolean;
	onHasEndTimeChange(value: boolean): void;
	infoAlerts?: ReactElement;
};

export const SettingsStep = (props: SettingsStepProps): ReactElement => {
	const {
		isActive,
		attachable,
		status,
		categoryHierarchy,
		isEditingExistingAnnouncement,
		hasPermissionToCreateNewCategory,
		hasPermissionToSendDirectPopups,
		onCreateNewCategory,
		timezone,
		hasStartTime,
		onHasStartTimeChange,
		hasEndTime,
		onHasEndTimeChange,
		infoAlerts
	} = props;

	const [
		isShowAnnouncementAutomaticallyAlertDismissed,
		setIsShowAnnouncementAutomaticallyAlertDismissed
	] = useState<boolean>(isEditingExistingAnnouncement);

	const showAutomatically: boolean = attachable.for("showAutomatically")
		.value;

	const isArchived: boolean = status === AnnouncementStatus.Archived;
	const isPublished: boolean = status === AnnouncementStatus.Published;
	const isDirectPopUpDisabled: boolean = isPublished
		|| isArchived
		|| !hasPermissionToSendDirectPopups;

	return (
		<Step
			isActive={isActive}
			trackingId="settings-step"
		>
			<Content>
				{infoAlerts}
				<FormContainer>
					<Title text={localize("_Settings")} />
					<FormItem
						label={localize("_Category(ies)")}
						isRequired
					>
						<AnnouncementCategorySelect
							attachable={attachable.for("categoryIds")}
							categoryHierarchy={categoryHierarchy}
							hasPermissionToCreateNewCategory={hasPermissionToCreateNewCategory}
							onCreateNewCategory={onCreateNewCategory}
							isArchived={isArchived}
							menuTitle={localize("_Category(ies)")}
						/>
						<ValidationError {...attachable.for("categoryIds").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_PinAnnouncement")}>
						<Checkbox
							{...attachable.for("isPinned").attachToCheckbox()}
							text={localize("_PinAnnouncementToTopOfCategoryPage")}
							isDisabled={isArchived}
						/>
						<ValidationError {...attachable.for("isPinned").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Tags")}>
						<HubTagSelect
							attachable={attachable.for("tags")}
							kind={TagKind.Announcement}
							isDisabled={isArchived}
							menuTitle={localize("_Tags")}
						/>

						<ValidationError {...attachable.for("tags").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_DirectPopup")}>
						<Checkbox
							{...attachable.for("showAutomatically").attachToCheckbox()}
							text={localize("_ShowPopupDirectlyToUser")}
							isDisabled={isDirectPopUpDisabled}
						/>
						<ValidationError {...attachable.for("showAutomatically").attachToValidationError()} />
						{showAutomatically && !isShowAnnouncementAutomaticallyAlertDismissed && (
							<AlertBar
								isInline
								kind="info"
								onDismiss={() => setIsShowAnnouncementAutomaticallyAlertDismissed(true)}
							>
								{localize("_DirectPopUpsMayHaveALargeImpactOnTheUserExperience")}
							</AlertBar>
						)}
					</FormItem>

					<FormItem label={localize("_Author")}>
						<Checkbox
							{...attachable.for("shouldShowAuthor").attachToCheckbox()}
							text={localize("_ShowAuthorOnAnnouncement")}
							isDisabled={isArchived}
						/>
						<ValidationError {...attachable.for("shouldShowAuthor").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Comments")}>
						<Checkbox
							{...attachable.for("areCommentsEnabled").attachToCheckbox()}
							text={localize("_AllowComments")}
							isDisabled={isArchived}
						/>
						<ValidationError {...attachable.for("areCommentsEnabled").attachToValidationError()} />
					</FormItem>
					<FormItem
						label={localize("_PublicationStart")}
						isForGroup
					>
						<RadioButtonGroup
							trackingId="publication-start"
							value={hasStartTime}
							onChange={onHasStartTimeChange}
							options={publicationStartOptions}
							isDisabled={isPublished || isArchived}
						/>
					</FormItem>
					{hasStartTime && (
						<FormItem label={localize("_SetStartDate")} isRequired>
							<DatePicker
								{...attachable.for("startTime").attachToDatePicker()}
								isDisabled={isPublished || isArchived}
								timezone={timezone}
							/>
							<ValidationError {...attachable.for("startTime").attachToValidationError()} />
						</FormItem>
					)}
					<FormItem
						label={localize("_PublicationEnd")}
						isForGroup
					>
						<RadioButtonGroup
							trackingId="publication-end"
							value={hasEndTime}
							onChange={onHasEndTimeChange}
							options={publicationEndOptions}
							isDisabled={isArchived}
						/>
					</FormItem>
					{hasEndTime && (
						<FormItem label={localize("_SetEndDate")} isRequired>
							<DatePicker
								{...attachable.for("endTime").attachToDatePicker()}
								isDisabled={isArchived}
								timezone={timezone}
							/>
							<ValidationError {...attachable.for("endTime").attachToValidationError()} />
						</FormItem>
					)}
				</FormContainer>
			</Content>
		</Step>
	);
};
SettingsStep.displayName = "SettingsStep";

type AnnouncementCategorySelectProps = {
	attachable: ICollectionInputAttachable<number, readonly number[]>;
	categoryHierarchy: readonly HubHierarchyListNodeModel[];
	hasPermissionToCreateNewCategory: boolean;
	isArchived: boolean;
	onCreateNewCategory(): void;
	menuTitle: string;
};

const AnnouncementCategorySelect = (props: AnnouncementCategorySelectProps): ReactElement => {
	const {
		hasPermissionToCreateNewCategory,
		isArchived,
		onCreateNewCategory,
		categoryHierarchy,
		attachable,
		menuTitle
	} = props;

	const multiSelectCategoryOptions = useMemo(
		(): readonly MultiSelectItem<number>[] => {
			return convertHubHierarchyListNodeToMultiSelectItem(categoryHierarchy);
		},
		[categoryHierarchy]
	);

	return (
		<InputRow>
			<InputRow.Stretch>
				<MultiSelect
					{...attachable.attachToMultiSelect()}
					options={multiSelectCategoryOptions}
					isDisabled={isArchived}
					trackingId="select-categories"
					menuTitle={menuTitle}
					doesSupportFiltering
					placeholderPropertyNames={[
						localize("_CategoryName")
					]}
				/>
			</InputRow.Stretch>

			{(hasPermissionToCreateNewCategory && !isArchived) && (
				<Button
					kind="secondary"
					text={localize("_New")}
					onClick={onCreateNewCategory}
					trackingId="create-new-category-button"
				/>
			)}
		</InputRow>
	);
};
AnnouncementCategorySelect.displayName = "AnnouncementCategorySelect";

const publicationStartOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		value: false,
		text: localize("_PublishNow")
	},
	{
		value: true,
		text: localize("_SetStartDate")
	}
];

const publicationEndOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		value: false,
		text: localize("_NoEndDate")
	},
	{
		value: true,
		text: localize("_SetEndDate")
	}
];

function convertHubHierarchyListNodeToMultiSelectItem(
	nodes: readonly HubHierarchyListNodeModel[]
): readonly MultiSelectItem<number>[] {
	const result: MultiSelectItem<number>[] = [];

	for (const node of nodes) {
		switch (node.kind) {
			case HubItemCategoryNodeKind.Category:
				const { category } = node;

				if (!category.canCreateItem) {
					continue;
				}

				result.push({
					value: category.id,
					text: category.name
				});
				break;
			case HubItemCategoryNodeKind.Group:
				const { name } = node;
				const children: readonly MultiSelectItem<number>[] = convertHubHierarchyListNodeToMultiSelectItem(node.children);

				if (children.length > 0) {
					result.push({
						uniqueId: newGuid(),
						text: name,
						children
					});
				}
				break;
			default:
				never(node);
		}
	}

	return result;
}